/**
 * FRONT-2594 — Canvas client storage: per-chat view state and per-item drafts.
 * Every access is try/catch'd; nothing here may throw, and the UI must render
 * correctly with storage absent (private window, blocked site data).
 */
import type { ArtifactDraft, SlideRow } from './artifact-edit-draft';

export const CANVAS_VIEW_PREFIX = 'eddy.canvas.v1.';
export const CANVAS_DRAFT_PREFIX = 'eddy.canvas.draft.v1.';

export type CanvasView = 'preview' | 'changes' | 'edit';

export interface CanvasViewState {
  open: boolean;
  activeKey: string | null;
  view: CanvasView;
}

export interface CanvasDraft {
  content: string;
  baseUpdatedAt: string | null;
}

export const DEFAULT_CANVAS_VIEW_STATE: CanvasViewState = { open: false, activeKey: null, view: 'preview' };

const VIEWS: readonly CanvasView[] = ['preview', 'changes', 'edit'];

const viewKey = (sessionId: string) => `${CANVAS_VIEW_PREFIX}${sessionId}`;
const draftKey = (sessionId: string, itemKey: string) => `${CANVAS_DRAFT_PREFIX}${sessionId}.${itemKey}`;

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort */
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
}

export function loadCanvasViewState(sessionId: string): CanvasViewState {
  const v = readJson(viewKey(sessionId));
  if (!v || typeof v !== 'object' || Array.isArray(v)) return { ...DEFAULT_CANVAS_VIEW_STATE };
  const r = v as Record<string, unknown>;
  return {
    open: r.open === true,
    activeKey: typeof r.activeKey === 'string' && r.activeKey ? r.activeKey : null,
    // A state stored while a read-only 'markdown' view existed (or anything else
    // unknown) reads as 'preview'.
    view: VIEWS.includes(r.view as CanvasView) ? (r.view as CanvasView) : 'preview',
  };
}

export function saveCanvasViewState(sessionId: string, state: CanvasViewState): void {
  write(viewKey(sessionId), state);
}

export function loadCanvasDraft(sessionId: string, itemKey: string): CanvasDraft | null {
  const v = readJson(draftKey(sessionId, itemKey));
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const r = v as Record<string, unknown>;
  if (typeof r.content !== 'string') return null;
  return { content: r.content, baseUpdatedAt: typeof r.baseUpdatedAt === 'string' ? r.baseUpdatedAt : null };
}

/**
 * FRONT-2598: a sketch's or a deck's draft (block edits + slide arrangement) under
 * the same per-chat, per-item key scheme as a document's, so the account-switch
 * purge covers it. Item keys never collide (`sketch:` / `deck:` vs `document:`).
 * Anything that does not parse as a draft reads as no draft.
 */
export function loadArtifactDraft(sessionId: string, itemKey: string): ArtifactDraft | null {
  const v = readJson(draftKey(sessionId, itemKey));
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const r = v as Record<string, unknown>;
  if (typeof r.base !== 'string' || !r.edits || typeof r.edits !== 'object' || Array.isArray(r.edits)) return null;
  const edits: ArtifactDraft['edits'] = {};
  for (const [id, e] of Object.entries(r.edits as Record<string, unknown>)) {
    const x = e as { original?: unknown; html?: unknown } | null;
    if (!x || typeof x.original !== 'string' || typeof x.html !== 'string') return null;
    edits[id] = { original: x.original, html: x.html };
  }
  let slides: SlideRow[] | undefined;
  if (r.slides !== undefined) {
    if (!Array.isArray(r.slides)) return null;
    slides = [];
    for (const s of r.slides) {
      const x = s as { from?: unknown; notes?: unknown; copy?: unknown } | null;
      if (!x || typeof x.from !== 'number' || typeof x.notes !== 'string') return null;
      slides.push({ from: x.from, notes: x.notes, ...(x.copy === true ? { copy: true } : {}) });
    }
  }
  return { base: r.base, edits, ...(slides ? { slides } : {}) };
}

export function saveArtifactDraft(sessionId: string, itemKey: string, draft: ArtifactDraft): void {
  write(draftKey(sessionId, itemKey), draft);
}

export function saveCanvasDraft(sessionId: string, itemKey: string, draft: CanvasDraft): void {
  write(draftKey(sessionId, itemKey), draft);
}

export function clearCanvasDraft(sessionId: string, itemKey: string): void {
  remove(draftKey(sessionId, itemKey));
}

/** Account-switch purge (lib/account-scope.ts): drops every Canvas view-state and draft key. */
export function clearAllCanvasStorage(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(CANVAS_VIEW_PREFIX) || k.startsWith(CANVAS_DRAFT_PREFIX))) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* best-effort */
  }
}

/** The active item if it still exists, else the newest item (last), else null. */
export function resolveActiveKey(activeKey: string | null, itemKeys: readonly string[]): string | null {
  if (activeKey && itemKeys.includes(activeKey)) return activeKey;
  return itemKeys.length ? itemKeys[itemKeys.length - 1]! : null;
}

export interface StoredViewState {
  sid: string;
  /** True once `state` came from storage (it is the default while the flag is off). */
  loaded: boolean;
  state: CanvasViewState;
}

/**
 * Keep the held view state in step with (chat, flag). It reloads from storage
 * when the chat changes AND when the flag flips off -> on: the flags query
 * usually resolves after the chat window mounts, so the state read at mount
 * (while still "off") would otherwise hide the person's saved open panel until
 * they touched something. Returns the same object when nothing changed.
 */
export function reconcileViewState(
  held: StoredViewState,
  sessionId: string,
  enabled: boolean,
  load: (sessionId: string) => CanvasViewState = loadCanvasViewState,
): StoredViewState {
  const sameSession = held.sid === sessionId;
  const canReuseState = held.loaded || !enabled;
  if (sameSession && canReuseState) return held;
  return { sid: sessionId, loaded: enabled, state: enabled ? load(sessionId) : DEFAULT_CANVAS_VIEW_STATE };
}
